import { Message } from '../types';

/**
 * Parses raw message content from Supabase to extract visual metadata.
 * Strips technical tags like [DELAYED:xxx], [SISTEMA ERR], and [TYPING].
 */
export const parseMessageContent = (rawContent: string, createdAtRaw: string) => {
    let content = (rawContent || '').trim();
    const upper = content.toUpperCase();

    // 1. FILTERING: Background technical flags that should be completely hidden
    if (
        upper.includes('[PROCESSING]') ||
        upper.includes('[TYPING]') ||
        upper.includes('[TYPING_INDICATOR]') ||
        upper.includes('[SYSTEM_EVENT:')
    ) {
        return null; // Signals that this message should be ignored completely
    }

    // 2. ERROR PARSING: Handle specific system tags
    if (upper.includes('[SISTEMA ERR]')) {
        content = content.replace(/\[SISTEMA ERR\]:?/i, '⚠️ [Error de Sistema]:').trim();
    }

    // 3. TARGET TIME PARSING: Handle [DELAYED:Timestamp]
    let isFuture = false;
    let revealTime = 0;
    const delayMatch = content.match(/\[DELAYED:([\d.]+)\]/);

    if (delayMatch) {
        revealTime = parseInt(delayMatch[1], 10);
        const now = Date.now();
        if (now < revealTime) isFuture = true;
        content = content.replace(delayMatch[0], '').trim();
    }

    // Strip [FETCHED_AT:Timestamp] tag
    content = content.replace(/\[FETCHED_AT:\d+\]/g, '').trim();

    // 4. ACTION TOKEN ROUTING: Extract special instructions for the app
    const actions: string[] = [];

    if (content.match(/\[ACTION:\s*UPDATE[-_\s]+DIET\s*\]/i)) {
        actions.push('UPDATE_DIET');
    }

    const programMatch = content.match(/\[ACTION:CHANGE_PROGRAM:([^\]]+)\]/i);
    if (programMatch) actions.push(`CHANGE_PROGRAM:${programMatch[1]}`);

    const swapMatch = content.match(/\[ACTION:SWAP_EXERCISE:([^\]]+)\]/i);
    if (swapMatch) actions.push(`SWAP_EXERCISE:${swapMatch[1]}`);

    const checkinMatch = content.match(/\[ACTION:CHANGE_CHECKIN_DAY:([^\]]+)\]/i);
    if (checkinMatch) actions.push(`CHANGE_CHECKIN_DAY:${checkinMatch[1]}`);
    
    // V1302: IDENTIFY DIET READY FLAG BEFORE STRIPPING
    const isDietReadyMsg = content.match(/\[MSG:DIET_READY\]/i) !== null;

    // Strip action tokens from UI
    content = content.replace(/\[ACTION:\s*UPDATE[-_\s]+DIET\s*\]/gi, '');
    content = content.replace(/\[ACTION:CHANGE_PROGRAM:[^\]]+\]/gi, '');
    content = content.replace(/\[ACTION:CHANGE_CHECKIN_DAY:[^\]]+\]/gi, '');
    content = content.replace(/\[ACTION:SWAP_EXERCISE(?::[^\]]+)?\]/gi, '');

    // Strip internal message type identifiers
    content = content.replace(/\[MSG:DIET_READY\]/gi, '');

    // 5. FAILSAVE: Strip trailing periods before emojis (Marc-specific styling rule)
    content = content.replace(/\.(?=\s?[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])/gu, '').trim();

    // 6. GHOST BUBBLE PREVENTION (V2027 FIX)
    // If the message only contained system tags (like [DELAYED] and [ACTION]), 
    // it will now be completely empty. But if it HAS actions, we must NOT return null,
    // otherwise the frontend drops the action completely.
    if (content.length === 0 && actions.length === 0 && !isDietReadyMsg) {
        return null; 
    }

    return { content, isFuture, revealTime, actions, isDietReadyMsg };
};

/**
 * Deduplicates messages to prevent visually identical responses appearing side-by-side
 * due to race conditions or network retries.
 */
export const isDuplicateMessage = (acc: Message[], m: any, cleanContent: string) => {
    // V2103: STRICTER DEDUPLICATION
    // Since parsed messages map `createdAt` to their future `revealTime`, comparing `prev.createdAt` 
    // against the raw DB `m.created_at` will fail for delayed diet messages (30 min gap).
    // If the content is identical and it's a diet ready message, it's ALWAYS a duplicate.
    return acc.some(prev => {
        if (prev.content === '[TYPING_INDICATOR]') return false;
        if (prev.content !== cleanContent || prev.role !== m.role) return false;
        
        // If it's a diet completion or checkin message, same content = duplicate only within 2 hours.
        if (prev.isDietReadyMsg || cleanContent.includes('Tu plan nutricional') || cleanContent.includes('chequeos semanales') || cleanContent.includes('He recalibrado todo')) {
            const prevTime = new Date(prev.createdAt || 0).getTime();
            const newTime = new Date(m.created_at).getTime();
            return Math.abs(prevTime - newTime) < 7200000; // 2 hours
        }

        // Otherwise, use a relaxed 60-minute window to catch any delayed message desyncs.
        const prevTime = new Date(prev.createdAt || 0).getTime();
        const newTime = new Date(m.created_at).getTime();
        return Math.abs(prevTime - newTime) < 3600000;
    });
};
